import { useQueryClient } from '@tanstack/react-query'
import { Clock, ListOrdered, Mail, Phone } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CacheKeys } from '@/lib/cache'
import { cn } from '@/lib/utils'
import {
  type ClosedLostDetails,
  SalesLeadStatus,
  salesLeadStatusColors,
  salesLeadStatusLabels,
  useSetFollowUpReminder,
  useTransitionLead,
} from '@/models/salesLead'
import { RecommendationAction, RecommendationPriority, type WorkQueueItem } from '@/models/workQueue'
import { ClosedLostReasonDialog } from '../SalesLeads/ClosedLostReasonDialog'

const SNOOZE_DAYS = 7

const priorityColors: Record<RecommendationPriority, string> = {
  [RecommendationPriority.HIGH]: 'bg-red-100 text-red-800',
  [RecommendationPriority.MEDIUM]: 'bg-amber-100 text-amber-800',
  [RecommendationPriority.LOW]: 'bg-slate-100 text-slate-700',
}

const actionIcon: Record<RecommendationAction, typeof Mail> = {
  [RecommendationAction.EMAIL]: Mail,
  [RecommendationAction.CALL]: Phone,
  [RecommendationAction.SEQUENCE]: ListOrdered,
  [RecommendationAction.STATUS_CHANGE]: Clock,
}

function whyBadge(item: WorkQueueItem): string {
  const { reason, daysSinceActivity, daysToExpiry } = item.recommendation.metadata
  if (reason === 'expiring' || reason === 'both') return `expiring ${daysToExpiry}d`
  if (daysSinceActivity != null) return `stalled ${daysSinceActivity}d`
  return 'needs attention'
}

export function WorkQueueRow({
  item,
  onDraftEmail,
}: {
  item: WorkQueueItem
  onDraftEmail: (item: WorkQueueItem) => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const transition = useTransitionLead()
  const setReminder = useSetFollowUpReminder()
  const [status, setStatus] = useState<SalesLeadStatus>(item.status)
  const [closingAsLost, setClosingAsLost] = useState(false)
  const rec = item.recommendation
  const ActionIcon = actionIcon[rec.action]

  const refreshQueue = () => queryClient.invalidateQueries({ queryKey: [CacheKeys.WORK_QUEUE] })

  function moveTo(targetStatus: SalesLeadStatus, details?: ClosedLostDetails) {
    setStatus(targetStatus)
    transition.mutate(
      { id: item.id, targetStatus, ...details },
      {
        onSuccess: refreshQueue,
        onError: () => {
          setStatus(item.status)
          toast.error('Failed to update status')
        },
      }
    )
  }

  const snooze = () =>
    setReminder.mutate(
      {
        id: item.id,
        reminderDate: new Date(Date.now() + SNOOZE_DAYS * 86_400_000).toISOString(),
        note: '',
      },
      { onSuccess: refreshQueue }
    )

  return (
    <div className="bg-surface flex flex-col gap-2 rounded-lg border p-4">
      <div className="flex items-center justify-between gap-2">
        <Link to={`/insure/internal/sales-leads/${item.id}`} className="font-medium hover:underline">
          {item.dbaName || item.legalName}
        </Link>
        <div className="flex items-center gap-2">
          <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', priorityColors[rec.priority])}>
            {rec.priority}
          </span>
          <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs">
            {whyBadge(item)}
          </span>
        </div>
      </div>

      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <ActionIcon className="size-4" />
        <span className="font-medium">{rec.action}</span>
        <span>— {rec.reasoning}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onDraftEmail(item)}>
          <Mail className="mr-1 size-4" /> Draft email
        </Button>
        <Button variant="outline" size="sm" onClick={snooze} disabled={setReminder.isPending}>
          <Clock className="mr-1 size-4" /> Snooze {SNOOZE_DAYS}d
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/insure/internal/sales-leads/${item.id}#sequences`)}
        >
          <ListOrdered className="mr-1 size-4" /> Start sequence
        </Button>
        <Select
          value={status}
          onValueChange={(v) => {
            if (v === status) return
            if (v === SalesLeadStatus.CLOSED_LOST) {
              setClosingAsLost(true)
              return
            }
            moveTo(v as SalesLeadStatus)
          }}
        >
          <SelectTrigger className="h-8 w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(SalesLeadStatus).map((s) => (
              <SelectItem
                key={s}
                value={s}
                disabled={s !== item.status && !item.availableTransitions.includes(s)}
              >
                <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', salesLeadStatusColors[s])}>
                  {salesLeadStatusLabels[s]}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <ClosedLostReasonDialog
        open={closingAsLost}
        onCancel={() => setClosingAsLost(false)}
        onConfirm={(details) => {
          setClosingAsLost(false)
          moveTo(SalesLeadStatus.CLOSED_LOST, details)
        }}
      />
    </div>
  )
}
